import styles from "./HomeInfoCards.module.scss";
import clx from "classnames";
import HoveredInfoIcon from "../../../Common/HoveredInfoIcon";
import { useDispatch, useSelector } from "react-redux";
import { getHomeStatistic } from "../../store/selectors";
import { getActiveSalon } from "../../../Auth/store/selectors";
import useIsPremiumUnlocked from "utils/useIsPremiumUnlocked";
import useWindowSize from "../../../../utils/useWindowSize";
import { Swiper, SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { statisticActions } from 'Containers/Statistic/store';
import { getStatisticsFinance, getStatisticsLoading, getStatisticsLockVersion } from 'Containers/Statistic/store/selectors';
import { mapFinanceToScope } from 'Containers/Statistic/containers/StatisticNew/apiMappers';
import { ROUTES } from 'routes/constants';
import { PREMIUM_UPGRADE_PATH } from 'utils/premiumUpgradePath';
import { salonRequiresStatsPassword } from 'utils/statsUnlock';
import { isDemoMode } from 'demo';
import StatsPasswordGate from 'Containers/Statistic/containers/StatisticNew/StatsPasswordGate';
import { healInteractionState } from 'utils/interactionRecovery';
import {
  buildDailyPerformanceInsights,
  buildDailyPerformanceInsightsFromScope,
  hasHomeStatMoneyInsights,
} from './buildDailyPerformanceInsights';
import PerformanceCard from './PerformanceCard';
import HomeStatsCard from './HomeStatsCard';
import { HomeInfoCardsSkeleton } from '../HomeSkeletons';

import 'swiper/css';

const SKIP_LOADER_NO_CACHE = { skipLoader: true, skipCache: true };
const HOME_FINANCE_PAYLOAD = { period: 'oggi', compare: 'precedente', poltrone: 'all' };

const HomeInfoCards = () => {
  const homeStat = useSelector(getHomeStatistic());
  const salon = useSelector(getActiveSalon());
  const finance = useSelector(getStatisticsFinance());
  const statsLoading = useSelector(getStatisticsLoading());
  const lockVersion = useSelector(getStatisticsLockVersion());
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [insightsRequested, setInsightsRequested] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const ignoreUnlockCloseRef = useRef(false);
  const hideStats = salon?.hideHomeStats === 1 || salon?.hideHomeStats === true;
  const passwordRequired = salonRequiresStatsPassword(salon) && !isDemoMode(salon);
  const isPremiumUnlocked = useIsPremiumUnlocked();

  const goToUpgrade = () => {
    navigate(PREMIUM_UPGRADE_PATH);
  };

  const fromHome = useMemo(() => buildDailyPerformanceInsights(homeStat, t), [homeStat, t]);
  const fromFinance = useMemo(() => {
    if (!insightsRequested || !finance || statsLoading.finance) return [];
    return buildDailyPerformanceInsightsFromScope(
      mapFinanceToScope(finance, { compareKey: 'precedente' }),
      t,
    );
  }, [finance, insightsRequested, statsLoading.finance, t]);

  const hasHomeMoney = hasHomeStatMoneyInsights(fromHome);
  const canLoadInsights = !hasHomeMoney && !insightsRequested;
  const canHideInsights = !hasHomeMoney && insightsRequested;
  const openPerformanceStats = useCallback(() => {
    navigate(ROUTES.STATISTICS);
  }, [navigate]);
  const performanceValues = hasHomeMoney
    ? fromHome
    : insightsRequested && fromFinance.length
      ? fromFinance
      : insightsRequested
        ? fromHome.map((row) => ({ ...row, count: '…' }))
        : fromHome;

  const fetchInsights = useCallback(() => {
    if (hasHomeMoney || insightsRequested) return;
    setInsightsRequested(true);
    dispatch(
      statisticActions.GET_STATISTICS_FINANCE.REQUEST(
        HOME_FINANCE_PAYLOAD,
        null,
        SKIP_LOADER_NO_CACHE,
      ),
    );
  }, [dispatch, hasHomeMoney, insightsRequested]);

  const closeUnlockModal = useCallback(() => {
    if (ignoreUnlockCloseRef.current) return;
    setUnlockOpen(false);
    healInteractionState({ reason: 'stats-unlock-close' });
  }, []);

  const loadPerformanceInsights = useCallback(() => {
    if (hasHomeMoney || insightsRequested) return;
    if (passwordRequired) {
      ignoreUnlockCloseRef.current = true;
      setUnlockOpen(true);
      window.setTimeout(() => {
        ignoreUnlockCloseRef.current = false;
      }, 400);
      return;
    }
    fetchInsights();
  }, [fetchInsights, hasHomeMoney, insightsRequested, passwordRequired]);

  const handleInsightsUnlocked = useCallback(() => {
    ignoreUnlockCloseRef.current = false;
    setUnlockOpen(false);
    healInteractionState({ reason: 'stats-unlock-ok' });
    if (hasHomeMoney) return;
    setInsightsRequested(true);
    dispatch(
      statisticActions.GET_STATISTICS_FINANCE.REQUEST(
        HOME_FINANCE_PAYLOAD,
        null,
        SKIP_LOADER_NO_CACHE,
      ),
    );
  }, [dispatch, hasHomeMoney]);

  const hidePerformanceInsights = useCallback(() => {
    setInsightsRequested(false);
  }, []);

  useEffect(() => {
    setUnlockOpen(false);
    setInsightsRequested(false);
  }, [salon?.idsalone]);

  useEffect(() => {
    if (lockVersion > 0) {
      setInsightsRequested(false);
    }
  }, [lockVersion]);

  useEffect(() => {
    if (!insightsRequested || statsLoading.finance || finance) return;
    setInsightsRequested(false);
  }, [insightsRequested, statsLoading.finance, finance]);

  const data = Object.keys(homeStat || {}).length
    ? [
      {
        cardName: t('home.infoCards.clients.name'),
        cardTheme: 'clients',
        subtitle: t('home.infoCards.clients.subtitle'),
        hide: salon?.hideHomeStats === 1 || salon?.hideHomeStats === true,
        disabled: false,
        linkTo: ROUTES.CLIENTS,
        footerCta: t('home.infoCards.clients.openClients'),
        values: [
          {
            id: 'total',
            title: t('home.infoCards.clients.total'),
            count: +homeStat.allClienti,
            value: false,
            isPercent: false,
            positive: false,
            infoDescription: t('home.infoCards.clients.totalInfo'),
          },
          {
            id: 'active',
            title: t('home.infoCards.clients.active'),
            count: +homeStat.activeUser,
            value: `${(+homeStat.diffActiveUser)?.toFixed(0)}%`,
            isPercent: true,
            positive: +homeStat.diffActiveUser >= 0,
            infoDescription: t('home.infoCards.clients.activeInfo'),
            deltaHint: t('home.infoCards.clients.activeDelta'),
          },
          {
            id: 'new',
            title: t('home.infoCards.clients.new'),
            count: +homeStat.allNewUserToday,
            value: `${(+homeStat.diffNewUserToday)?.toFixed(0)}%`,
            isPercent: true,
            positive: +homeStat.diffNewUserToday >= 0,
            infoDescription: t('home.infoCards.clients.newInfo'),
            deltaHint: t('home.infoCards.clients.newDelta'),
          },
        ],
      },
      {
        cardName: t('home.infoCards.bookings.name'),
        cardTheme: 'bookings',
        subtitle: t('home.infoCards.bookings.subtitle'),
        hide: salon?.hideHomeStats === 1 || salon?.hideHomeStats === true,
        disabled: false,
        linkTo: ROUTES.SCHEDULER,
        footerCta: t('home.infoCards.bookings.openCalendar'),
        values: [
          {
            id: 'total',
            title: t('home.infoCards.bookings.total'),
            count: +homeStat.allPren,
            value: false,
            isPercent: false,
            positive: false,
            infoDescription: t('home.infoCards.bookings.totalInfo'),
          },
          {
            id: 'week',
            title: t('home.infoCards.bookings.week'),
            count: +homeStat.allPrenThisWeek,
            value: `${(+homeStat.diffPrenThisWeek)?.toFixed(0)}%`,
            isPercent: true,
            positive: +homeStat.diffPrenThisWeek >= 0,
            infoDescription: t('home.infoCards.bookings.weekInfo'),
            deltaHint: t('home.infoCards.bookings.weekDelta'),
          },
          {
            id: 'today',
            title: t('home.infoCards.bookings.today'),
            count: +homeStat.allPrenToday,
            value: `${(+homeStat.diffPrentoday)?.toFixed(0)}%`,
            isPercent: true,
            positive: +homeStat.diffPrentoday >= 0,
            infoDescription: t('home.infoCards.bookings.todayInfo'),
            deltaHint: t('home.infoCards.bookings.todayDelta'),
          },
        ],
      },
      {
        cardName: t('home.infoCards.performance.name'),
        cardTheme: 'performance',
        subtitle: t('home.infoCards.performance.subtitle'),
        hide: salon?.hideHomeStats === 1 || salon?.hideHomeStats === true,
        disabled: false,
        linkTo: ROUTES.STATISTICS,
        footerCta: t('home.infoCards.performance.openStats'),
        values: performanceValues,
      },
    ]
    : [];

  const visibleCards = data.filter((card) => !(card.hide && salon));

  const renderCardBody = (card) => {
    const stopBubbleProps = {
      onClick: (event) => event.stopPropagation(),
      onKeyDown: (event) => event.stopPropagation(),
    };

    if (card.cardTheme === 'performance') {
      return (
        <PerformanceCard
          card={card}
          stopBubbleProps={stopBubbleProps}
          compact={isMobile}
          isPremiumUnlocked={isPremiumUnlocked}
          onUpgrade={goToUpgrade}
          premiumLabel={t('statistics.premiumLock.title')}
          canLoadInsights={canLoadInsights}
          canHideInsights={canHideInsights}
          onLoadInsights={loadPerformanceInsights}
          onHideInsights={hidePerformanceInsights}
          loadInsightsLabel={t('home.infoCards.performance.loadInsights')}
          hideInsightsLabel={t('home.infoCards.performance.hideInsights')}
          onOpenStats={openPerformanceStats}
        />
      );
    }
    if (card.cardTheme) {
      return <HomeStatsCard card={card} stopBubbleProps={stopBubbleProps} compact={isMobile} />;
    }

    return (
      <>
        <div className={styles.card_name}>
          <span>{card.cardName}</span>
        </div>
        <div className={styles.card_values_container}>
          {card.values.map((value) => (
            <div key={`${card.cardName}${value.title}`} className={styles.card_value_item}>
              <div className={styles.item_title}>{value.title}</div>
              <div className={styles.item_values_wrapper}>
                <div className={styles.item_count}>{value.count}</div>
                <div className={styles.value_and_info_container} {...stopBubbleProps}>
                  <div
                    className={clx(
                      styles.value_wrapper,
                      { [styles.disabled]: !value.value },
                      { [styles.percent]: value.isPercent },
                      { [styles.positive]: value.positive },
                    )}
                  >
                    <div className={styles.value_count}>{value.value}</div>
                  </div>
                  <HoveredInfoIcon description={value.infoDescription} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const isCardNavigable = (card) =>
    !!card.linkTo && !(card.cardTheme === 'performance' && canLoadInsights);

  const getCardClassName = (card) =>
    clx(styles.card_item_wrapper, {
      [styles.disabled_card]: !!card?.disabled,
      [styles.stats_card]: !!card.cardTheme,
      [styles.stats_card_clients]: card.cardTheme === 'clients',
      [styles.stats_card_bookings]: card.cardTheme === 'bookings',
      [styles.performance_card]: card.cardTheme === 'performance',
      [styles.card_clickable]: isCardNavigable(card),
    });

  const getCardInteractionProps = (card) => {
    if (!isCardNavigable(card)) return {};
    return {
      onClick: () => navigate(card.linkTo),
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(card.linkTo);
        }
      },
      role: 'button',
      tabIndex: 0,
    };
  };

  const modalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null;
  const unlockModal =
    unlockOpen && modalRoot
      ? createPortal(
          <div
            className={styles.insights_unlock_overlay}
            data-stats-unlock-open="true"
            onClick={closeUnlockModal}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <div
              className={styles.insights_unlock_panel}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className={styles.insights_unlock_close}
                onClick={closeUnlockModal}
                aria-label={t('common.actions.close')}
              >
                ×
              </button>
              <StatsPasswordGate compact onUnlocked={handleInsightsUnlocked} />
            </div>
          </div>,
          modalRoot,
        )
      : null;

  if (hideStats) return null;

  if (!Object.keys(homeStat || {}).length) {
    return (
      <div ref={rootRef}>
        <HomeInfoCardsSkeleton isMobile={isMobile} />
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
      <div
        ref={rootRef}
        className={clx(styles.cards_container, styles.cards_container_mobile)}
        data-stats-password={passwordRequired ? '1' : '0'}
      >
          {visibleCards.map((card) => (
            <div
              key={card.cardName}
              className={getCardClassName(card)}
              {...getCardInteractionProps(card)}
            >
              {renderCardBody(card)}
            </div>
          ))}
        </div>
        {unlockModal}
      </>
    );
  }

  return (
    <div ref={rootRef} data-stats-password={passwordRequired ? '1' : '0'}>
      {unlockModal}
      <Swiper
        style={{ overflow: 'visible' }}
        grabCursor={true}
        slidesPerView={3}
        spaceBetween={20}
        breakpoints={{
          0: {
            slidesPerView: 1.12,
            spaceBetween: 12,
          },
          600: {
            slidesPerView: 2.12,
            spaceBetween: 16,
          },
          900: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
        }}
        className={styles.cards_container}
      >
        {visibleCards.map((card) => (
          <SwiperSlide
            key={card.cardName}
            className={getCardClassName(card)}
            {...getCardInteractionProps(card)}
          >
            {renderCardBody(card)}
            <div className={styles.disabled_card_value}>{t('home.infoCards.inDevelopment')}</div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default HomeInfoCards;
